import { useEffect, useState } from "react";
import {
  AlertTriangle, Scale, Check, CheckCircle2, Circle, Wrench, RotateCw,
  Clock, HelpCircle, FileCheck2, ArrowLeft, ArrowRight, Sparkles, ChevronDown,
} from "lucide-react";

// BLOCK 3D · Sub-Schritt C · Wiederverwendung der bestehenden Lehrbild-Runtime.
// KEIN neuer Renderer, KEINE neue Komponente — exakt dasselbe Muster wie
// `frontend/src/vsi/renderers.jsx::HazardMarkersRenderer` (Zeile 93-105).
import {
  LehrbildImage,
  setActiveLehrbildWorld,
  startLehrbildManifestPolling,
} from "@/vsi/lehrbild";

const RISK_LABEL = { gering: "gering", mittel: "mittel", hoch: "hoch" };

// BLOCK 3D · Sub-Schritt C · sichtbares Maximum an Lehrbild-Slots pro
// anonymer /lp3-Vorschau (Businessentscheidung: 15). Faktisch existieren im
// WeltDokument bislang nur bis zu 5 fachlich passende Slots (Hazards) —
// dieser Cap ist deshalb aktuell eine harte Sicherheitsgrenze, keine Ober-
// grenze, die üblicherweise erreicht wird.
const LP3_MAX_VISIBLE_LEHRBILDER = 15;

function HazardCard({ h }) {
  return (
    <div className="haz">
      <div className="haz-head">
        <span className="haz-cat"><AlertTriangle size={13} /> {h.category}</span>
        <span className={`risk risk-${h.residual_risk}`}>Restrisiko: {RISK_LABEL[h.residual_risk] || h.residual_risk}</span>
      </div>
      {/* BLOCK 3D · Sub-Schritt C · Lehrbild-Slot (Priorität 1: GBU-Hazard).
          Referenzmuster: renderers.jsx::HazardMarkersRenderer (Zeile 93-105).
          Der Manifest-Lookup nutzt `h.description` (Klartext = deutscher
          Quelltext im LP3-Kontext, ohne Übersetzung). Fehlt das Bild oder ist
          es noch nicht generiert, erscheint ein neutraler, screenreader-
          neutraler Skeleton mit ausschließlich vorhandenen Tailwind-Klassen
          (`bg-slate-100 animate-pulse`) im selben aspect-[16/9]-Rahmen. */}
      <LehrbildImage
        text={h.description}
        testId={`lp3-hazard-lehrbild-${h.description ? String(h.description).slice(0, 40) : "nil"}`}
        className="mb-3 aspect-[16/9] overflow-hidden rounded-lg border border-amber-200 bg-white"
        fallback={
          <div
            aria-hidden="true"
            className="mb-3 aspect-[16/9] overflow-hidden rounded-lg border border-amber-200 bg-slate-100 animate-pulse"
            data-testid="lp3-hazard-lehrbild-skeleton"
          />
        }
      />
      <div className="haz-desc">{h.description}</div>
      <div className="haz-measures">
        {h.measures_technical && <div className="m-item"><span>Technisch</span>{h.measures_technical}</div>}
        {h.measures_organizational && <div className="m-item"><span>Organisatorisch</span>{h.measures_organizational}</div>}
        {h.measures_personal && <div className="m-item"><span>Persönlich · PSA</span>{h.measures_personal}</div>}
      </div>
    </div>
  );
}

const Field = ({ k, v }) => (
  <div className="wd-field"><div className="wd-field-k">{k}</div><div className="wd-field-v">{v}</div></div>
);
const FieldList = ({ k, items }) => (
  <div className="wd-field"><div className="wd-field-k">{k}</div>
    <ul className="wd-field-list">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
  </div>
);

// LP4 – Komplette Dokumentationswelt als EIN durchgehendes Dokument (keine Tabs).
export default function WeltDokument({ world, onAssign, onNew }) {
  // LP4.1 – Lesefortschritt für Scroll-Guide (rein clientseitig, kein Backend).
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const cur = window.scrollY || doc.scrollTop;
      setPct(max > 0 ? Math.min(Math.round((cur / max) * 100), 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [world]);
  const atAssign = pct >= 90;

  const uw = world.unterweisung;
  const ba = world.betriebsanweisung;
  const gbu = world.gbu;
  const questions = world.questions || [];
  const wartung = world.wartung;
  const wdh = world.wiederholung || {};

  // BLOCK 3D · Sub-Schritt C · Lehrbild-Slot-Planung.
  // Sichtbare Slot-Liste = Priorität 1 (GBU-Hazards). WeltDokument rendert
  // aktuell KEINE R/F- oder Measure-Karten mit eigener visueller Struktur,
  // deren Lehrbild-Zuordnung im regulären `renderers.jsx` existiert — daher
  // bleiben Priorität 2/3 im /lp3-WeltDokument leer. Sicherheits-Cap: max. 15.
  const hazardsAll = Array.isArray(gbu?.hazards) ? gbu.hazards : [];
  const visibleHazards = hazardsAll.slice(0, LP3_MAX_VISIBLE_LEHRBILDER);

  // BLOCK 3D · Sub-Schritt C · aktive Lehrbild-Welt setzen + Manifest-Polling
  // starten, solange noch nicht alle sichtbaren Slots aufgelöst sind. Polling
  // endet automatisch bei Timeout (120 s), vollständigem Manifest, Unmount
  // oder Wechsel der `world_id`.
  const worldId = world?.world_id || null;
  useEffect(() => {
    if (!worldId) return undefined;
    setActiveLehrbildWorld(worldId);
    const expected = visibleHazards
      .map((h) => (h && h.description ? String(h.description) : ""))
      .filter(Boolean);
    const stop = startLehrbildManifestPolling(worldId, expected, {
      intervalMs: 3000,
      timeoutMs: 120000,
    });
    return () => stop();
    // Bewusst nur `worldId` als Dependency — die sichtbaren Slot-Texte sind
    // durch das `world`-Objekt an dieselbe `worldId` gebunden und ändern sich
    // nur bei Weltwechsel (dann wird der Effekt ohnehin neu aufgesetzt).
  }, [worldId]);

  const bausteine = [
    ["Gefährdungsbeurteilung (GBU)", !!gbu],
    ["Betriebsanweisung", !!ba],
    ["Unterweisung", uw.blocks.length > 0],
    [`Verständnisfragen (${questions.length})`, questions.length > 0],
    ["Prüf- / Wartungshinweise", !!wartung],
    [`Wiedervorlage-Intervall (${uw.recurrence})`, true],
  ];

  return (
    <section className="wrap reveal in" data-testid="lp2-preview">
      <div className="wd-guide" data-testid="lp4-guide">
        <div className="wd-guide-row">
          <span className="wd-guide-msg">
            <Sparkles size={15} /> Aus einem Begriff wurde Ihre vollständige Dokumentationswelt. Scrollen Sie bis zur automatischen Zuweisung.
          </span>
          <span className="wd-guide-step" data-testid="lp4-guide-step">
            {atAssign ? "Schritt 4 von 4: Automatische Zuweisung" : "Schritt 3 von 4: Dokument prüfen"}
          </span>
        </div>
        <div className="wd-guide-bar"><span style={{ width: `${pct}%` }} /></div>
      </div>

      <div className="weltdoc" data-testid="lp4-weltdoc">
        <div className="wd-topbar">
          <button className="wd-back" data-testid="lp4-new" onClick={onNew}><ArrowLeft size={16} /> Neue Vorschau erzeugen</button>
        </div>

        <header className="wd-head" data-testid="lp4-head">
          <span className="typ">{world.typ}</span>
          <h1 className="wd-title">{world.title}<span className="wd-suffix"> – Dokumentationswelt</span></h1>
          {world.short_description && <p className="wd-desc">{world.short_description}</p>}
        </header>

        <section className="wd-section">
          <h2 className="wd-h2">Enthaltene Bausteine dieser Dokumentationswelt</h2>
          <ul className="wd-bausteine">
            {bausteine.map(([label, on], i) => (
              <li key={i} className={on ? "on" : "off"}>
                {on ? <CheckCircle2 size={17} /> : <Circle size={17} />}<span>{label}</span>
              </li>
            ))}
          </ul>
        </section>

        {gbu && (
          <section className="wd-section">
            <h2 className="wd-h2">Gefährdungsbeurteilung</h2>
            <p className="wd-resp">
              <Scale size={14} />{" "}
              {/* Schritt 4b: legal_basis kommt als list[str] (Reihenfolge = Backend).
                  Lesbar kommasepariert; fehlt/leer → nichts anzeigen (kein [], kein
                  undefined/null, kein Crash). KEINE Sortierung/Filterung/Umordnung. */}
              {Array.isArray(gbu.legal_basis) && gbu.legal_basis.length > 0 && (
                <>{gbu.legal_basis.join(", ")} · </>
              )}
              Verantwortlich: {gbu.responsible_role}
            </p>
            {visibleHazards.map((h, i) => <HazardCard h={h} key={i} />)}
          </section>
        )}

        {ba && (
          <section className="wd-section">
            <h2 className="wd-h2">Betriebsanweisung</h2>
            <div className="wd-ba">
              {ba.purpose && <Field k="Anwendungsbereich" v={ba.purpose} />}
              {ba.main_hazards?.length > 0 && <FieldList k="Hauptgefahren" items={ba.main_hazards} />}
              {ba.protective_measures?.length > 0 && <FieldList k="Schutzmaßnahmen · PSA" items={ba.protective_measures} />}
              {ba.behavior_in_emergency && <Field k="Verhalten im Notfall" v={ba.behavior_in_emergency} />}
              {ba.first_aid && <Field k="Erste Hilfe" v={ba.first_aid} />}
              {ba.disposal_or_storage && <Field k="Entsorgung / Lagerung" v={ba.disposal_or_storage} />}
            </div>
          </section>
        )}

        <section className="wd-section">
          <h2 className="wd-h2">Unterweisung</h2>
          <div className="uw-meta">
            <span className="badge"><RotateCw size={13} /> Wiederholung: {uw.recurrence}</span>
            <span className="badge"><Clock size={13} /> ~{uw.duration_minutes} Min. Lesezeit</span>
          </div>
          {uw.recurrence_reason && <p className="uw-reason">{uw.recurrence_reason}</p>}
          {uw.blocks.map((b, i) => (
            <div className="wd-uw" key={i}>
              <div className="wd-uw-h">{i + 1}. {b.heading}</div>
              <div className="wd-uw-b">{b.body}</div>
            </div>
          ))}
        </section>

        {questions.length > 0 && (
          <section className="wd-section">
            <h2 className="wd-h2"><HelpCircle size={18} /> Verständnisfragen</h2>
            {questions.map((q, i) => (
              <div className="wd-q" key={i}>
                <div className="wd-q-t">{i + 1}. {q.text}</div>
                <ul className="wd-q-opts">
                  {q.options.map((o, j) => (
                    <li key={j} className={j === q.correct_index ? "correct" : ""}>
                      {j === q.correct_index ? <Check size={14} /> : <span className="rad" />}
                      <span>{o}</span>
                      {j === q.correct_index && <em>richtige Antwort</em>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        )}

        {wartung && (
          <section className="wd-section">
            <h2 className="wd-h2">Prüf- und Wartungshinweise</h2>
            {wartung.items.map((it, i) => (
              <div className="fr" key={i}>
                <div className="fr-l">
                  <Wrench size={15} />
                  <div><span className="w">{it.description}</span><small>Intervall: {it.interval} · {it.who}</small></div>
                </div>
              </div>
            ))}
            {wartung.next_check && <p className="wd-note">Nächste Sachkundigen-Prüfung empfohlen in: {wartung.next_check}</p>}
          </section>
        )}

        <section className="wd-section">
          <h2 className="wd-h2">Wiedervorlage &amp; Wiederholungsintervalle</h2>
          <div className="wd-rec-grid">
            <div className="wd-rec"><div className="k">Unterweisung</div><div className="v">{wdh.unterweisung}</div></div>
            {wdh.gbu_review && <div className="wd-rec"><div className="k">GBU-Überprüfung</div><div className="v">{wdh.gbu_review}</div></div>}
            {wdh.wartung && <div className="wd-rec"><div className="k">Wartungszyklus</div><div className="v">{wdh.wartung}</div></div>}
          </div>
        </section>

        <section className="wd-section">
          <h2 className="wd-h2">Nachweise</h2>
          <p className="nw-intro">{world.nachweis.intro}</p>
          <div className="nw-grid">
            {world.nachweis.points.map((p, i) => (
              <div className="nw-item" key={i}>
                <FileCheck2 size={18} />
                <div><div className="t">{p.title}</div><div className="m">{p.text}</div></div>
              </div>
            ))}
          </div>
        </section>

        <div className="wd-cta">
          <p><strong>Ihre Dokumentationswelt ist fertig.</strong> CLartext weist sie jetzt automatisch den betroffenen Mitarbeitern zu – Sie müssen nichts auswählen.</p>
          <button className="btn btn-primary btn-lg wd-assign pulse" data-testid="lp2-assign" onClick={onAssign}>
            Weiter <ArrowRight size={18} />
          </button>
        </div>
      </div>

      {pct < 80 && (
        <div className="wd-scrollhint" data-testid="lp4-scrollhint">
          <span>Weiter nach unten scrollen</span>
          <ChevronDown size={16} className="bounce" />
        </div>
      )}
    </section>
  );
}
